import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { useContext, useEffect } from 'react';
import { LanguageContext } from '../contexts/LanguageContext';

// Fallback translations when context is not available
const fallbackTranslations = {
  unknownError: 'An unexpected error occurred',
  pageNotFound: 'Page Not Found',
  pageNotFoundDetails: 'The page you are looking for does not exist.',
  unauthorized: 'Unauthorized',
  unauthorizedDetails: 'You need to be logged in to access this page.',
  forbidden: 'Forbidden',
  forbiddenDetails: 'You do not have permission to access this resource.',
  serverError: 'Server Error',
  serverErrorDetails: 'Something went wrong on our end. Please try again later.',
  technicalDetails: 'Technical Details',
  reload: 'Reload Page',
  goHome: 'Go to Home',
  persistentIssue: 'If the issue persists, please contact support.'
};

export function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  
  // Try to use context, but provide fallback if not available
  const context = useContext(LanguageContext as any);
  const t = (key: string) => {
    if (context?.t) {
      return context.t(`errorBoundary.${key}`);
    }
    return fallbackTranslations[key as keyof typeof fallbackTranslations] || key;
  };
  const isLoading = context?.isLoading || false;

  // Log error to console for debugging
  useEffect(() => {
    console.error('========================================');
    console.error('ERROR BOUNDARY CAUGHT AN ERROR:');
    console.error('========================================');
    console.error('Error object:', error);
    console.error('Error type:', typeof error);
    console.error('Is RouteError:', isRouteErrorResponse(error));
    console.error('Is Error instance:', error instanceof Error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    console.error('========================================');
  }, [error]);

  // Wait for i18n to load
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  let errorMessage = t('unknownError');
  let errorStatus = '';
  let errorDetails = '';

  if (isRouteErrorResponse(error)) {
    errorStatus = `${error.status}`;
    errorMessage = error.statusText || errorMessage;
    
    if (error.status === 404) {
      errorMessage = t('pageNotFound');
      errorDetails = t('pageNotFoundDetails');
    } else if (error.status === 401) {
      errorMessage = t('unauthorized');
      errorDetails = t('unauthorizedDetails');
    } else if (error.status === 403) {
      errorMessage = t('forbidden');
      errorDetails = t('forbiddenDetails');
    } else if (error.status === 500) {
      errorMessage = t('serverError');
      errorDetails = t('serverErrorDetails');
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = error.stack || '';
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <div className="flex flex-col items-center text-center">
          {/* Error Icon */}
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-red-600" />
          </div>

          {/* Error Status */}
          {errorStatus && (
            <div className="text-6xl font-bold text-slate-900 mb-4">
              {errorStatus}
            </div>
          )}

          {/* Error Message */}
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            {errorMessage}
          </h1>

          {/* Error Details */}
          {errorDetails && (
            <p className="text-slate-600 mb-8 max-w-md">
              {errorDetails}
            </p>
          )}

          {/* Development Mode - Show Stack Trace */}
          {import.meta.env.DEV && error instanceof Error && error.stack && (
            <details className="w-full mb-8 text-left">
              <summary className="cursor-pointer text-sm font-semibold text-slate-700 hover:text-slate-900 mb-2">
                {t('technicalDetails')}
              </summary>
              <pre className="bg-slate-100 p-4 rounded-lg text-xs overflow-x-auto text-slate-800">
                {error.stack}
              </pre>
            </details>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <RefreshCw className="w-5 h-5" />
              {t('reload')}
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-300 transition-colors font-medium"
            >
              <Home className="w-5 h-5" />
              {t('goHome')}
            </button>
          </div>

          {/* Help Text */}
          <p className="text-sm text-slate-500 mt-8">
            {t('persistentIssue')}
          </p>
        </div>
      </div>
    </div>
  );
}